import React, { useState } from 'react';
import {
  Box,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  Tooltip,
  Typography,
} from '@mui/material';
import SwapHorizIcon from '@material-ui/icons/SwapHoriz';
import CloseIcon from '@material-ui/icons/Close';
import { PackLine } from '../../../services/fulfillment/types';
import { groupBinsByZone } from '../../../services/fulfillment/utils';

interface Props {
  line: PackLine;
  actions?: React.ReactNode;
  note?: React.ReactNode;
}

const PackLineItem: React.FC<Props> = ({ line, actions, note }) => {
  const [locationsOpen, setLocationsOpen] = useState(false);
  const [relocateOpen, setRelocateOpen] = useState(false);

  const primaryBin =
    line.binLocations.find(b => b.locationType === 'pick') ?? line.binLocations[0] ?? null;

  return (
    <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'flex-start', width: '100%' }}>
      {/* Thumbnail */}
      <Box
        sx={{
          width: 52,
          height: 52,
          flexShrink: 0,
          bgcolor: 'grey.100',
          borderRadius: 0.5,
          overflow: 'hidden',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          border: '1px solid',
          borderColor: 'grey.200',
        }}
      >
        {line.imageUrl ? (
          <img
            src={line.imageUrl}
            alt={line.sku}
            style={{ width: '100%', height: '100%', objectFit: 'contain' }}
          />
        ) : (
          <Typography sx={{ fontSize: 22, lineHeight: 1 }}>
            {line.itemType === 'bike' || line.itemType === 'frame'
              ? '🚲'
              : line.itemType === 'wheel'
              ? '⭕'
              : '📦'}
          </Typography>
        )}
      </Box>

      {/* Details */}
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.25, mb: 0.125 }}>
          <Typography sx={{ fontSize: 13, fontWeight: 700, letterSpacing: '0.3px' }}>
            {line.sku}
          </Typography>
          {line.binLocations.length > 0 && (
            <Tooltip title="All locations">
              <IconButton
                size="small"
                onClick={e => {
                  e.stopPropagation();
                  setLocationsOpen(true);
                }}
                sx={{ p: 0.25, color: 'grey.400', '&:hover': { color: 'primary.main' } }}
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="14px"
                  height="14px"
                  viewBox="0 -960 960 960"
                  fill="currentColor"
                >
                  <path d="M120-40v-880h80v80h560v-80h80v880h-80v-80H200v80h-80Zm80-480h80v-160h240v160h240v-240H200v240Zm0 320h240v-160h240v160h80v-240H200v240Zm160-320h80v-80h-80v80Zm160 320h80v-80h-80v80ZM360-520h80-80Zm160 320h80-80Z" />
                </svg>
              </IconButton>
            </Tooltip>
          )}
          <Tooltip title="Relocate item">
            <IconButton
              size="small"
              onClick={e => {
                e.stopPropagation();
                setRelocateOpen(true);
              }}
              sx={{ p: 0.25, color: 'grey.400', '&:hover': { color: 'warning.main' } }}
            >
              <SwapHorizIcon style={{ fontSize: 14 }} />
            </IconButton>
          </Tooltip>
        </Box>

        <Typography sx={{ fontSize: 11, color: 'grey.600' }} noWrap>
          {line.title}
        </Typography>

        {line.upc && (
          <Typography sx={{ fontSize: 11, mt: 0.25 }}>
            <Box component="span" sx={{ fontWeight: 700, color: 'grey.600', mr: '4px' }}>
              UPC
            </Box>
            <Box component="span" sx={{ color: 'grey.500' }}>
              {line.upc}
            </Box>
          </Typography>
        )}

        {line.mpn && (
          <Typography sx={{ fontSize: 11 }}>
            <Box component="span" sx={{ fontWeight: 700, color: 'grey.600', mr: '4px' }}>
              MPN
            </Box>
            <Box component="span" sx={{ color: 'grey.500' }}>
              {line.mpn}
            </Box>
          </Typography>
        )}

        {primaryBin && (
          <Typography sx={{ fontSize: 11, color: 'grey.700', mt: 0.25 }}>
            <Box component="span" sx={{ mr: '3px' }}>
              📍
            </Box>
            <Box component="span" sx={{ fontWeight: 600 }}>
              {primaryBin.binCode}
            </Box>
            <Box component="span" sx={{ color: 'grey.400', ml: '4px' }}>
              · qty {primaryBin.quantity}
            </Box>
          </Typography>
        )}

        {note}
      </Box>

      {/* Right-side actions */}
      {actions && (
        <Box sx={{ flexShrink: 0, display: 'flex', alignItems: 'center' }}>{actions}</Box>
      )}

      {/* All Locations dialog */}
      <Dialog open={locationsOpen} onClose={() => setLocationsOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle
          sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', pb: 1 }}
        >
          <Box>
            <Typography
              sx={{
                fontSize: 11,
                fontWeight: 700,
                color: 'grey.500',
                textTransform: 'uppercase',
                letterSpacing: '0.5px',
              }}
            >
              Inventory Locations
            </Typography>
            <Typography sx={{ fontSize: 16, fontWeight: 700 }}>{line.sku}</Typography>
          </Box>
          <IconButton size="small" onClick={() => setLocationsOpen(false)}>
            <CloseIcon fontSize="small" />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ pt: 0 }}>
          {(() => {
            const zoneGroups = groupBinsByZone(line.binLocations);
            if (zoneGroups.length === 0) {
              return (
                <Typography sx={{ fontSize: 13, color: 'grey.500', py: 1 }}>
                  No inventory locations found.
                </Typography>
              );
            }
            return (
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, py: 1 }}>
                {zoneGroups.map(group => (
                  <Box key={group.zoneLabel}>
                    <Typography
                      sx={{
                        fontSize: 11,
                        fontWeight: 700,
                        color: 'grey.500',
                        textTransform: 'uppercase',
                        letterSpacing: '0.5px',
                        mb: 0.75,
                      }}
                    >
                      {group.zoneLabel}
                    </Typography>
                    {group.bins.map(bin => (
                      <Box
                        key={bin.binCode}
                        sx={{
                          display: 'flex',
                          alignItems: 'baseline',
                          justifyContent: 'space-between',
                          py: 0.5,
                          borderBottom: '1px solid',
                          borderColor: 'grey.100',
                        }}
                      >
                        <Typography sx={{ fontSize: 14, fontWeight: 600, color: 'grey.800' }}>
                          {bin.binCode}
                        </Typography>
                        <Typography sx={{ fontSize: 12, color: 'grey.500' }}>
                          qty {bin.quantity}
                        </Typography>
                      </Box>
                    ))}
                  </Box>
                ))}
              </Box>
            );
          })()}
        </DialogContent>
      </Dialog>

      {/* Relocate dialog (stub) */}
      <Dialog open={relocateOpen} onClose={() => setRelocateOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle
          sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', pb: 1 }}
        >
          <Box>
            <Typography
              sx={{
                fontSize: 11,
                fontWeight: 700,
                color: 'grey.500',
                textTransform: 'uppercase',
                letterSpacing: '0.5px',
              }}
            >
              Relocate
            </Typography>
            <Typography sx={{ fontSize: 16, fontWeight: 700 }}>{line.sku}</Typography>
          </Box>
          <IconButton size="small" onClick={() => setRelocateOpen(false)}>
            <CloseIcon fontSize="small" />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          <Typography sx={{ fontSize: 13, color: 'grey.500', py: 1 }}>
            Relocate will be wired to the inventory API in the production build.
          </Typography>
        </DialogContent>
      </Dialog>
    </Box>
  );
};

export default PackLineItem;
